<template>
  <div>
    <div
      class="h-15 text-xl font-semibold px-6 py-4 border-b border-neutral-border-default bg-neutral-background-default"
    >
      App Settings
    </div>

    <div class="flex">
      <div class="border-r border-mid-neutral-50 bg-white">
        <Tabs
          v-model:tab="activeStepNumber"
          :tabs="steps"
          :style="4"
          class="h-full"
          @update:step="updateStep"
        />
      </div>
      <div class="flex-1 flex flex-col">
        <div class="px-6 py-4 bg-white border-b border-mid-neutral-50 h-20">
          <div v-if="activeStep" class="space-y-1">
            <p class="text-base font-semibold text-dark-neutral-500">
              {{ activeStep.title }} Configuration Form
            </p>
            <p v-if="activeStep.subtitle" class="text-sm text-mid-neutral-400">
              Fill out the necessary informations below to configure your
              {{ activeStep.title.toLowerCase() }}.
            </p>
          </div>
        </div>
        <div class="flex-1 p-6">
          <div class="bg-white rounded-lg shadow-sm p-6">
            <template v-if="activeStepNumber === Tab.AI_ASSISTANT">
              <AiAssistantSettings />
            </template>
            <template v-else-if="activeStepNumber === Tab.STRATEGY">
              <StrategySettings />
            </template>
            <template v-else-if="activeStepNumber === Tab.EXTENSION">
              <ExtensionSettings />
            </template>
            <template v-else-if="activeStepNumber === Tab.LOGS">
              <LogsSettings />
            </template>
            <template v-else-if="activeStepNumber === Tab.PDF_PURCHASE">
              <PdfPurchaseSettings />
            </template>
            <template v-else-if="activeStepNumber === Tab.ABOUT">
              <div class="text-sm">version: {{ version }}</div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Tabs from "@app/components/Global/Tabs/Tabs.vue";
import AiAssistantSettings from "./AiAssistantSettings.vue";
import ExtensionSettings from "./ExtensionSettings.vue";
import LogsSettings from "./LogsSettings.vue";
import PdfPurchaseSettings from "./PdfPurchaseSettings.vue";
import { useRoute, useRouter } from "vue-router";
import { computed, ref } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { AuthKey, DevToolboxKey } from "@app/injectionKeys";
import { Role } from "@core/domain/types/role.type";
import {
  BookOpenIcon,
  CommandLineIcon,
  InformationCircleIcon,
  PuzzlePieceIcon,
  SparklesIcon,
  DocumentIcon,
} from "@heroicons/vue/24/outline";
import StrategySettings from "./StrategySettings.vue";

const version = import.meta.env.VITE_EVIDENCE_VERSION;
const { user } = injectStrict(AuthKey);
const { isCuttingEdge } = injectStrict(DevToolboxKey);
enum Tab {
  AI_ASSISTANT,
  STRATEGY,
  LOGS,
  PDF_PURCHASE,
  EXTENSION,
  ABOUT,
}

const steps = [
  ...(user.value?.role === Role.ADMIN
    ? [
        {
          number: Tab.AI_ASSISTANT,
          title: "AI Assistant",
          subtitle: "Manage your AI assistant settings for a personalized experience.",
          icon: SparklesIcon,
        },
        ...(isCuttingEdge
          ? [
              {
                number: Tab.STRATEGY,
                title: "Strategy",
                icon: BookOpenIcon,
              },
            ]
          : []),
        ...(isCuttingEdge
          ? [
              {
                number: Tab.PDF_PURCHASE,
                title: "PDF Purchase",
                icon: DocumentIcon,
              },
            ]
          : []),
        {
          number: Tab.LOGS,
          title: "Logs",
          icon: CommandLineIcon,
        },
      ]
    : []),

  ...(user.value?.role !== Role.READ_ONLY
    ? [
        {
          number: Tab.EXTENSION,
          title: "Extension",
          icon: PuzzlePieceIcon,
        },
      ]
    : []),
  {
    number: Tab.ABOUT,
    title: "About",
    icon: InformationCircleIcon,
  },
];

const route = useRoute();
const router = useRouter();
const activeStepNumber = ref(steps[0].number);
const activeSubStepNumber = ref(1);
const activeStep = computed(() => {
  return steps.find((step) => step.number === activeStepNumber.value);
});

if (route.query.tab) {
  activeStepNumber.value = Number(route.query.tab);
}
if (route.query.subtab) {
  activeSubStepNumber.value = Number(route.query.subtab);
}

function updateStep(step: number) {
  router.push({ query: { tab: step, subtab: 1 } });
}
</script>
